import type { AiContextData, AiContextJsonObject, AiContextJsonValue, AiContextRegistrationData } from './types';

type AiContextDataSnapshot = {
    readonly normalizedData: AiContextData;
    readonly serializedData: string;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
    const prototype = Object.getPrototypeOf(value);

    return prototype === Object.prototype || prototype === null;
};

const isAiContextJsonObject = (value: AiContextJsonValue | undefined): value is AiContextJsonObject => {
    return value !== undefined && value !== null && typeof value === 'object' && Array.isArray(value) === false;
};

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: will be reduced once we have backend responses
const normalizeAiContextValue = (value: unknown): AiContextJsonValue | undefined => {
    if (value === null) {
        return null;
    }

    if (typeof value === 'boolean' || typeof value === 'string') {
        return value;
    }

    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : undefined;
    }

    if (Array.isArray(value)) {
        const normalizedValues: AiContextJsonValue[] = [];

        for (const item of value) {
            const normalizedItem = normalizeAiContextValue(item);

            if (normalizedItem === undefined) {
                return undefined;
            }

            normalizedValues.push(normalizedItem);
        }

        return normalizedValues;
    }

    if (typeof value !== 'object' || !isPlainObject(value)) {
        return undefined;
    }

    const normalizedObject: Record<string, AiContextJsonValue> = {};

    for (const [key, nestedValue] of Object.entries(value)) {
        const normalizedNestedValue = normalizeAiContextValue(nestedValue);

        if (normalizedNestedValue === undefined) {
            return undefined;
        }

        normalizedObject[key] = normalizedNestedValue;
    }

    return normalizedObject satisfies AiContextJsonObject;
};

export const createAiContextDataSnapshot = (data: AiContextRegistrationData): AiContextDataSnapshot | null => {
    const normalizedValue = normalizeAiContextValue(data);

    if (!isAiContextJsonObject(normalizedValue)) {
        return null;
    }

    return {
        normalizedData: normalizedValue,
        serializedData: JSON.stringify(normalizedValue),
    };
};
